import {
  CollectionForMetadata,
  CollectionForMetadataVariables,
  CollectionForMetadata_collection,
  CollectionPageContents_me,
} from 'globalTypes'
import { NextPage } from 'next'
import type { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import requestIp from 'request-ip'

import { Provider } from 'Root/Contexts/Ip'
import Collection from 'Root/Pages/Collection'
import VisitTracker from 'Root/Pages/Collection/Component/VisitTracker'
import apolloClient from 'lib/apollo-client'
import getElementServerSideProps from 'lib/get-element-server-side-props'
import getQueryString from 'lib/get-query-string'
import { handleNotFoundOnMissingDataOrAuthentication } from 'lib/handle-not-found-on-missing-data-or-authentication'
import { loadCatalog } from 'lib/i18n'
import filter from 'lodash/filter'
import map from 'lodash/map'

import { defaultFavicons } from '../../../next-seo.config'
import QUERY from './collection-for-metadata.graphql'

interface Props {
  collection: CollectionForMetadata_collection | null
  me: CollectionPageContents_me | null
  ip: string | null
}

const Page: NextPage<Props> = (props: Props) => {
  const collection = props.collection
  const me = props.me
  const images = map(filter([collection.cover]), (url) => ({
    url,
  }))

  const brandName = collection.creator?.brandName
    ? collection.creator.brandName
    : 'Taylora'

  const pageFavicon = collection.creator?.brandLogoSmall
    ? [
        {
          rel: 'icon',
          href: collection.creator.brandLogoSmall,
        },
      ]
    : defaultFavicons

  const DefaultSeo = () => (
    <NextSeo
      additionalMetaTags={[
        {
          name: 'msapplication-TileColor',
          content: '#ffffff',
        },
        {
          name: 'theme-color',
          content: '#ffffff',
        },
      ]}
      title={`${collection.title} – ${brandName}`}
      description={collection.description}
      openGraph={{
        images,
      }}
      additionalLinkTags={pageFavicon}
    />
  )

  return (
    <Provider value={props.ip}>
      <DefaultSeo />
      <VisitTracker id={collection.id} />
      <Collection ssCollection={collection} ssMe={me} />
    </Provider>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    identifier,
    token: access_token,
    authorizationToken,
  } = getElementServerSideProps(context)

  const translation = await loadCatalog(getQueryString(context.query.locale))

  const { data } = await apolloClient.query<
    CollectionForMetadata,
    CollectionForMetadataVariables
  >({
    query: QUERY,
    variables: {
      identifier,
      token: authorizationToken,
    },
    context: {
      headers: {
        Authorization: Boolean(access_token) ? `Bearer ${access_token}` : '',
      },
    },
  })

  if (!Boolean(data?.collection)) {
    return handleNotFoundOnMissingDataOrAuthentication(
      context.req.url,
      access_token
    )
  }

  return {
    props: {
      collection: data.collection,
      me: data.me,
      ip: requestIp.getClientIp(context.req),
      translation: translation,
    } as Props,
  }
}

export default Page
